export type FlashElementOutlineOptions = {
  color?: string;
  scrollIntoView?: boolean;
};

const HOLD_DURATION_MS = 800;
const FADE_DURATION_MS = 1500;

export function flashElementOutline(
  element: HTMLElement,
  { color = '#facc15', scrollIntoView = true }: FlashElementOutlineOptions = {}
): () => void {
  if (scrollIntoView) {
    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  const resolvedColor = resolveCssColor(element, color);
  const previousOutline = element.style.outline;
  const previousOutlineOffset = element.style.outlineOffset;
  const previousBorderRadius = element.style.borderRadius;
  let restored = false;

  element.style.outline = `2px solid ${resolvedColor}`;
  element.style.outlineOffset = '4px';
  element.style.borderRadius = '8px';

  const restore = () => {
    if (restored) return;
    restored = true;
    element.style.outline = previousOutline;
    element.style.outlineOffset = previousOutlineOffset;
    element.style.borderRadius = previousBorderRadius;
  };

  if (typeof element.animate !== 'function') {
    const timeout = window.setTimeout(
      restore,
      HOLD_DURATION_MS + FADE_DURATION_MS
    );
    return () => {
      window.clearTimeout(timeout);
      restore();
    };
  }

  const animation = element.animate(
    [
      { outlineColor: resolvedColor, offset: 0 },
      {
        outlineColor: resolvedColor,
        offset: HOLD_DURATION_MS / (HOLD_DURATION_MS + FADE_DURATION_MS),
      },
      { outlineColor: 'transparent', offset: 1 },
    ],
    {
      duration: HOLD_DURATION_MS + FADE_DURATION_MS,
      easing: 'ease-out',
    }
  );
  animation.addEventListener('finish', restore, { once: true });

  return () => {
    animation.cancel();
    restore();
  };
}

function resolveCssColor(element: HTMLElement, color: string): string {
  const match = color.match(/^var\(\s*(--[^,\s)]+)(?:,\s*([^)]+))?\s*\)$/u);
  if (!match) return color;
  return (
    getComputedStyle(element).getPropertyValue(match[1]).trim() ||
    match[2]?.trim() ||
    color
  );
}
